import * as React from "react";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useApiClient } from "../../../api";
import Autocomplete from "@mui/material/Autocomplete";
import { enqueueSnackbar } from "notistack";

function DomainNamesInput({ value, setValue }) {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]} // no predefined options
      value={value}
      onChange={(event, newValue) => {
        // Strip any leading '@' from each string
        const sanitized = newValue.map((v) =>
          v.startsWith("@") ? v.slice(1) : v,
        );
        setValue(sanitized);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={index}
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={"Email domains"}
          helperText="List possible domain names for school emails"
        />
      )}
    />
  );
}

export const SchoolConfigDialog = ({
  open,
  setOpen,
  school,
  onComplete = () => {},
}) => {
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isApproved, setIsApproved] = useState(
    school?.subscription?.isSchoolApproved || false,
  );
  const [subscriptions, setSubscriptions] = useState([]);
  const [domains, setDomains] = useState([]);
  const ApiClient = useApiClient();

  const [confirmArchive, setConfirmArchive] = useState(false);

  const updateSubscription = async (acknowledgeArchive = false) => {
    if (
      isApproved &&
      !school?.subscription?.isSchoolApproved &&
      !acknowledgeArchive
    ) {
      setConfirmArchive(true);
      return;
    }
    try {
      const result = await ApiClient.schools.configureSchoolSubscription({
        school: school.id,
        domains,
        subscriptionType: selectedSubscription,
        isApproved: isApproved,
        ...(acknowledgeArchive ? { acknowledgeArchive: true } : {}),
      });
      if (result.success) {
        enqueueSnackbar(result.message, {
          variant: "success",
          autoHideDuration: 3000,
        });
        handleClose();
      } else {
        enqueueSnackbar(result.message, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("There was a problem saving the configuration", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleClose = () => {
    setConfirmArchive(false);
    setOpen(false);
    onComplete();
  };

  useEffect(() => {
    const fetchSchoolSubscriptionTypes = async () => {
      try {
        const result =
          await ApiClient.subscriptions.getSchoolSubscriptionTypes();
        if (result) {
          setSubscriptions(result || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchSchoolSubscriptionTypes();
  }, []);

  useEffect(() => {
    setDomains(school ? school.emailDomains?.map((d) => d.domain) : []);
    setSelectedSubscription(school ? school.subscriptionStatus : "");
    setIsApproved(school?.subscription?.isSchoolApproved || false);
  }, [school]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"md"}>
        <DialogTitle>Add school domains and subscription status</DialogTitle>
        <DialogContent>
          <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="subscription-status-label">
                Subscription Status
              </InputLabel>
              <Select
                labelId="subscription-status-label"
                label={"Subscription Status"}
                value={selectedSubscription || ""}
                onChange={(e) => setSelectedSubscription(e.target.value)}
              >
                {subscriptions.map((sub) => (
                  <MenuItem key={sub} value={sub}>
                    {sub.charAt(0).toUpperCase() + sub.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DomainNamesInput value={domains} setValue={setDomains} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isApproved}
                  onChange={(e) => setIsApproved(e.target.checked)}
                />
              }
              label="School approved"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant={"outlined"}>
            Cancel
          </Button>
          <Button
            variant={"contained"}
            onClick={() => updateSubscription()}
            disabled={domains?.length === 0 || !selectedSubscription}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <DialogTitle>Archive existing school data?</DialogTitle>
        <DialogContent>
          Approving this school will archive all existing classes and the
          students in those classes. Existing work will no longer be accessible
          in Fruitify. Teacher accounts will be retained.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmArchive(false);
              updateSubscription(true);
            }}
          >
            Archive and approve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
